import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { WalletContext } from './WalletContext';
import './BankDetails.css';

function BankDetails({ onBack }) {
  const [formData, setFormData] = useState({
    bankName: '',
    accountHolderName: '',
    accountNumber: '',
    confirmAccountNumber: '',
    ifscCode: '',
    upiId: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { walletBalance } = useContext(WalletContext);

  // Fetch bank details on component mount
  useEffect(() => {
    const fetchBankDetails = async () => {
      try {
        setLoading(true);
        const userData = JSON.parse(localStorage.getItem('user'));

        if (!userData || !userData.id) {
          setError('User not found');
          setLoading(false);
          return;
        }

        const response = await fetch(
          `https://backend.mainbazar777.site/api/withdrawal-detail?id=${userData.id}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch bank details');
        }

        const data = await response.json();

        if (data.success && data.user) {
          setFormData(prev => ({
            ...prev,
            bankName: data.user.bank_name || '',
            accountHolderName: data.user.user_name || '',
            accountNumber: data.user.account_number || '',
            confirmAccountNumber: data.user.account_number || '',
            ifscCode: data.user.ifsc_code || '',
            upiId: data.user.upi_id || ''
          }));
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching bank details:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchBankDetails();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    const { upiId, bankName, accountHolderName, accountNumber, confirmAccountNumber, ifscCode } = formData;

    const hasUPI = upiId.trim() !== '';
    const hasBankDetails = bankName.trim() !== '' || accountHolderName.trim() !== '' ||
                          accountNumber.trim() !== '' || ifscCode.trim() !== '';

    // If neither UPI nor bank details are provided
    if (!hasUPI && !hasBankDetails) {
      toast.error('Please fill either UPI ID or bank details');
      return;
    }

    // If bank details are partially filled
    if (hasBankDetails) {
      if (!bankName || !accountHolderName || !accountNumber || !confirmAccountNumber || !ifscCode) {
        toast.error('Please fill all bank details fields');
        return;
      }

      if (accountNumber !== confirmAccountNumber) {
        toast.error('Account numbers do not match');
        return;
      }
    }

    try {
      const userData = JSON.parse(localStorage.getItem('user'));

      if (!userData || !userData.id) {
        toast.error('User not found');
        return;
      }

      // Build payload
      const payload = {
        user_id: userData.id
      };

      // Add bank details if provided
      if (hasBankDetails) {
        payload.bank_name = bankName;
        payload.ifsc_code = ifscCode;
        payload.account_number = accountNumber;
        payload.user_name = accountHolderName;
      }

      // Add UPI ID if provided
      if (hasUPI) {
        payload.upi_id = upiId;
      }

      const response = await fetch(
        'https://backend.mainbazar777.site/api/withdrawal-detail-update',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update withdrawal details');
      }

      const data = await response.json();

      if (data.success) {
        toast.success(data.message || 'Withdrawal details updated successfully!');
      } else {
        toast.error(data.message || 'Failed to update withdrawal details');
      }
    } catch (err) {
      console.error('Error updating withdrawal details:', err);
      toast.error('Error: ' + err.message);
    }
  };

  return (
    <div className="bank-details-page">
      {/* Header */}
      <div className="bank-details-header">
        <button className="back-btn" onClick={onBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
        <h1>Bank</h1>
        <button className="wallet-btn" onClick={() => navigate("/funds")}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <rect x="2" y="6" width="20" height="14" rx="2" stroke="white" strokeWidth="2"/>
            <path d="M2 10H22" stroke="white" strokeWidth="2"/>
            <circle cx="18" cy="15" r="1.5" fill="white"/>
          </svg>
          <span className="wallet-balance">₹ {walletBalance}</span>
        </button>
      </div>

      {/* Content */}
      <div className="bank-details-content">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading bank details...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <p className="error-message">⚠️ {error}</p>
            <p className="error-note">Please try again later or contact support</p>
          </div>
        ) : (
          <>

        {/* Bank Name */}
        <div className="form-field">
          <label className="field-label">Bank Name</label>
          <div className="input-wrapper">
            <div className="field-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M3 21H21M4 18H20V10H4V18ZM12 3L2 8H22L12 3Z" stroke="#666" strokeWidth="1.5" fill="none"/>
                <line x1="7" y1="12" x2="7" y2="16" stroke="#666" strokeWidth="1.5"/>
                <line x1="12" y1="12" x2="12" y2="16" stroke="#666" strokeWidth="1.5"/>
                <line x1="17" y1="12" x2="17" y2="16" stroke="#666" strokeWidth="1.5"/>
              </svg>
            </div>
            <input
              type="text"
              name="bankName"
              className="field-input"
              placeholder="Enter bank name"
              value={formData.bankName}
              onChange={handleChange}
            />
          </div>
        </div>

        {/* Account Holder Name */}
        <div className="form-field">
          <label className="field-label">Account Holder Name</label>
          <div className="input-wrapper">
            <div className="field-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="8" r="4" stroke="#666" strokeWidth="1.5"/>
                <path d="M4 20C4 16 7 14 12 14C17 14 20 16 20 20" stroke="#666" strokeWidth="1.5"/>
              </svg>
            </div>
            <input
              type="text"
              name="accountHolderName"
              className="field-input"
              placeholder="Enter account holder name"
              value={formData.accountHolderName}
              onChange={handleChange}
            />
          </div>
        </div>

        {/* Account Number */}
        <div className="form-field">
          <label className="field-label">Account Number</label>
          <div className="input-wrapper">
            <div className="field-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="7" width="18" height="12" rx="2" stroke="#666" strokeWidth="1.5"/>
                <path d="M3 11H21" stroke="#666" strokeWidth="1.5"/>
                <rect x="6" y="14" width="4" height="2" rx="0.5" fill="#666"/>
              </svg>
            </div>
            <input
              type="number"
              name="accountNumber"
              className="field-input"
              placeholder="Enter account number"
              value={formData.accountNumber}
              onChange={handleChange}
            />
          </div>
        </div>

        {/* Confirm Account Number */}
        <div className="form-field">
          <label className="field-label">Confirm Account Number</label>
          <div className="input-wrapper">
            <div className="field-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="7" width="18" height="12" rx="2" stroke="#666" strokeWidth="1.5"/>
                <path d="M3 11H21" stroke="#666" strokeWidth="1.5"/>
                <path d="M8 15L10 17L15 12" stroke="#666" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
            <input
              type="number"
              name="confirmAccountNumber"
              className="field-input"
              placeholder="Re-enter account number"
              value={formData.confirmAccountNumber}
              onChange={handleChange}
            />
          </div>
        </div>

        {/* IFSC Code */}
        <div className="form-field">
          <label className="field-label">IFSC Code</label>
          <div className="input-wrapper">
            <div className="field-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <rect x="4" y="6" width="16" height="12" rx="2" stroke="#666" strokeWidth="1.5"/>
                <path d="M8 10H16M8 14H12" stroke="#666" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
            <input
              type="text"
              name="ifscCode"
              className="field-input"
              placeholder="Enter IFSC code"
              value={formData.ifscCode}
              onChange={handleChange}
            />
          </div>
        </div>

        {/* Divider */}
        <div style={{ margin: '20px 0', textAlign: 'center', color: '#999' }}>
          <span>OR</span>
        </div>

        {/* UPI ID */}
        <div className="form-field">
          <label className="field-label">UPI ID</label>
          <div className="input-wrapper">
            <div className="field-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M3 6H21V18C21 19.1046 20.1046 20 19 20H5C3.89543 20 3 19.1046 3 18V6Z" stroke="#666" strokeWidth="1.5"/>
                <path d="M3 10H21" stroke="#666" strokeWidth="1.5"/>
              </svg>
            </div>
            <input
              type="text"
              name="upiId"
              className="field-input"
              placeholder="Enter UPI ID (e.g., 9988776662@upi)"
              value={formData.upiId}
              onChange={handleChange}
            />
          </div>
        </div>

        {/* Submit Button */}
        <button className="submit-btn" onClick={handleSubmit}>
          SUBMIT
        </button>
          </>
        )}
      </div>
    </div>
  );
}

export default BankDetails;
